import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Building2,
  Landmark,
  CheckCircle2,
  Loader2,
  Scale,
} from "lucide-react";
import { bankAccountsApi, chartOfAccountsApi, taxSettingsApi } from "../../services/api";

interface BankAccount {
  _id: string;
  kind: "trust" | "operating";
  name: string;
  institution: string | null;
  accountLast4: string | null;
  currency: string;
  status: "open" | "closed";
  openedAt: string;
}

interface BalanceSummary {
  totalAssetsCents: number;
  totalLiabilitiesCents: number;
  totalEquityCents: number;
  totalRevenueCents: number;
  totalExpensesCents: number;
}

function formatCents(cents: number): string {
  const isNegative = cents < 0;
  const abs = Math.abs(cents);
  const formatted = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(abs / 100);
  return isNegative ? `-${formatted}` : formatted;
}

function formatDate(iso: string | null): string {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const OperatingOverviewTab: React.FC = () => {
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [summary, setSummary] = useState<BalanceSummary>({
    totalAssetsCents: 0,
    totalLiabilitiesCents: 0,
    totalEquityCents: 0,
    totalRevenueCents: 0,
    totalExpensesCents: 0,
  });
  const [taxSummary, setTaxSummary] = useState<{ collectedCents: number; itcCents: number; netCents: number }>({
    collectedCents: 0, itcCents: 0, netCents: 0,
  });
  const [taxMode, setTaxMode] = useState<string>("hst");
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Compute current quarter boundaries for tax summary
      const now = new Date();
      const quarterMonth = Math.floor(now.getMonth() / 3) * 3;
      const periodStart = new Date(now.getFullYear(), quarterMonth, 1).toISOString().split("T")[0];
      const periodEnd = new Date(now.getFullYear(), quarterMonth + 3, 0).toISOString().split("T")[0];

      const [accountsRes, balancesRes, taxRes, taxSettingsRes] = await Promise.all([
        bankAccountsApi.list({ kind: "operating", status: "open" }),
        chartOfAccountsApi.balances(),
        taxSettingsApi.summary({ periodStart, periodEnd }).catch(() => ({ data: { taxCollectedCents: 0, inputTaxCreditsCents: 0, netRemittanceCents: 0 } })),
        taxSettingsApi.get().catch(() => ({ data: { taxMode: "hst" } })),
      ]);
      setAccounts(accountsRes.data?.data || []);
      const raw = balancesRes.data?.summary || {};
      setSummary({
        totalAssetsCents: raw.totalAssetsCents || 0,
        totalLiabilitiesCents: raw.totalLiabilitiesCents || 0,
        totalEquityCents: raw.totalEquityCents || 0,
        totalRevenueCents: raw.totalRevenueCents || 0,
        totalExpensesCents: raw.totalExpensesCents || 0,
      });
      const taxData = taxRes.data || {};
      setTaxSummary({
        collectedCents: taxData.taxCollectedCents || 0,
        itcCents: taxData.inputTaxCreditsCents || 0,
        netCents: taxData.netRemittanceCents || 0,
      });
      setTaxMode(taxSettingsRes.data?.taxMode || "hst");
    } catch (err) {
      console.error("Failed to load operating overview:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const summaryCards = [
    {
      label: "Total Assets",
      value: formatCents(summary.totalAssetsCents),
      Icon: DollarSign,
      color: "text-emerald-700",
      bg: "bg-emerald-50",
    },
    {
      label: "Total Revenue",
      value: formatCents(summary.totalRevenueCents),
      Icon: TrendingUp,
      color: "text-blue-700",
      bg: "bg-blue-50",
    },
    {
      label: "Total Expenses",
      value: formatCents(summary.totalExpensesCents),
      Icon: TrendingDown,
      color: "text-red-700",
      bg: "bg-red-50",
    },
    {
      label: "Total Liabilities",
      value: formatCents(summary.totalLiabilitiesCents),
      Icon: Building2,
      color: "text-amber-700",
      bg: "bg-amber-50",
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {summaryCards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
          >
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${card.bg}`}>
                <card.Icon className={`h-5 w-5 ${card.color}`} />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">{card.label}</p>
                <p className={`text-lg font-bold ${card.color}`}>{card.value}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Tax Summary */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 rounded-lg bg-violet-50">
            <Scale className="h-5 w-5 text-violet-700" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-800">
              {taxMode === "gst_pst" ? "GST/PST" : taxMode === "gst_only" ? "GST" : "HST"} Summary
            </h3>
            <p className="text-xs text-slate-400">Current period</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-slate-500 mb-1">Tax Collected</p>
            <p className="text-lg font-bold text-slate-800">{formatCents(taxSummary.collectedCents)}</p>
            <p className="text-[10px] text-slate-400">From invoices issued</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-1">Input Tax Credits</p>
            <p className="text-lg font-bold text-emerald-700">{formatCents(taxSummary.itcCents)}</p>
            <p className="text-[10px] text-slate-400">From expenses & bills</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-1">Net Remittance</p>
            <p className={`text-lg font-bold ${taxSummary.netCents > 0 ? "text-red-700" : taxSummary.netCents < 0 ? "text-emerald-700" : "text-slate-800"}`}>
              {formatCents(Math.abs(taxSummary.netCents))}
            </p>
            <p className="text-[10px] text-slate-400">{taxSummary.netCents > 0 ? "Owing to CRA" : taxSummary.netCents < 0 ? "Refund expected" : "Balanced"}</p>
          </div>
        </div>
      </div>

      {/* Operating Bank Accounts */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4">Operating Bank Accounts</h2>
        {accounts.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
            <Landmark className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="text-lg font-medium text-slate-600">No operating accounts</p>
            <p className="text-sm text-slate-400 mt-1">
              Use the "Add Account" button above to create your first operating account
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {accounts.map((account, i) => (
              <motion.div
                key={account._id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-purple-100">
                      <Building2 className="h-5 w-5 text-purple-700" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">{account.name}</h3>
                      {account.institution && (
                        <p className="text-xs text-slate-500">{account.institution}</p>
                      )}
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                    <CheckCircle2 className="h-3 w-3" />
                    Open
                  </span>
                </div>

                <div className="space-y-2">
                  {account.accountLast4 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Account</span>
                      <span className="text-slate-700 font-mono">...{account.accountLast4}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Currency</span>
                    <span className="text-slate-700">{account.currency}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Opened</span>
                    <span className="text-slate-700">{formatDate(account.openedAt)}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OperatingOverviewTab;
